import React, { type ReactNode } from 'react';
import { cssMap, cx } from '@atlaskit/css';
import { Flex, Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

type Props = {
	leftContent: ReactNode;
	rightContent: ReactNode;
	footer: ReactNode;
};

type PanelsLayoutProps = {
	leftContent: ReactNode;
	rightContent: ReactNode;
};

const styles = cssMap({
	container: {
		height: '100%',
		width: '100%',
	},
	inline: {
		overflow: 'hidden',
	},
	inlineWithFixedHeight: {
		overflow: 'hidden',
		height: '376px',
	},
	panel: {
		paddingBlock: token('space.250'),
		paddingInline: token('space.250'),
		overflow: 'hidden',
	},
	panelWithSwitcher: {
		paddingBlockEnd: token('space.300'),
		paddingInline: token('space.250'),
		overflow: 'hidden',
	},
	leftPanel: {
		flexBasis: '240px',
		borderRight: `${token('border.width')} solid ${token('color.border')}`,
	},
	leftPanelWithSwitcher: {
		flexBasis: '240px',
		position: 'relative',
		paddingLeft: token('space.0'),
		'&::after': {
			content: '""',
			position: 'absolute',
			right: 0,
			top: token('space.0'),
			bottom: token('space.300'),
			width: '1px',
			// can't use color.border here but this is the same color as the color.border
			backgroundColor: token('color.background.neutral.hovered'),
		},
	},
	rightPanel: {
		flex: 1,
	},
	rightPanelWithSwitcher: {
		flex: 1,
		paddingRight: token('space.0'),
	},
});

export const Layout = ({ leftContent, rightContent, footer }: Props) => (
	<Stack grow="fill" spread="space-between" xcss={styles.container}>
		<Inline alignBlock="stretch" grow="fill" xcss={styles.inline}>
			<Flex xcss={cx(styles.panel, styles.leftPanel)}>{leftContent}</Flex>
			<Flex xcss={cx(styles.panel, styles.rightPanel)}>{rightContent}</Flex>
		</Inline>
		{footer}
	</Stack>
);

export const PanelsLayout = ({ leftContent, rightContent }: PanelsLayoutProps) => (
	<Inline alignBlock="stretch" grow="fill" xcss={styles.inlineWithFixedHeight}>
		<Flex xcss={cx(styles.panelWithSwitcher, styles.leftPanelWithSwitcher)}>{leftContent}</Flex>
		<Flex xcss={cx(styles.panelWithSwitcher, styles.rightPanelWithSwitcher)}>{rightContent}</Flex>
	</Inline>
);
